import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { ArrowBack as BackIcon, Check as CheckIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useCallback, useRef, useState, type ReactNode } from 'react';

/**
 * Multi-step linear flow primitive.
 *
 * Each step renders its own form and registers a "next handler" through
 * `controller.setNextHandler(fn)`. When the user clicks the footer's
 * Next button, the wizard awaits that handler — if it returns `true`,
 * the wizard advances; `false` keeps the user on the same step
 * (useful when the API call fails and we want to surface the toast
 * but stay put). If a step doesn't register a handler, plain advance.
 *
 * Three flows in PR-D use it: ABHA Aadhaar OTP, Care Context linking,
 * Consent raise.
 */

export interface WizardStep {
  id: string;
  title: string;
  hint?: string;
  content: (controller: WizardStepController) => ReactNode;
}

export type NextHandler = () => boolean | Promise<boolean>;

export interface WizardStepController {
  /** Go back. First step → `onCancel()`. */
  prev: () => void;
  /** Abort the wizard from inside a step. */
  cancel: () => void;
  /** Pin the action button enabled-state. */
  setNextEnabled: (enabled: boolean) => void;
  /** Replace the next button label (e.g. "Send OTP" instead of "Next"). */
  setNextLabel: (label: ReactNode) => void;
  /**
   * Register the function the wizard runs when the user clicks Next.
   * Return `true` to advance, `false` to stay (e.g. on API error).
   * Calling with `null` clears the handler — wizard advances unconditionally.
   */
  setNextHandler: (fn: NextHandler | null) => void;
}

export function Wizard({
  steps,
  onComplete,
  onCancel,
}: {
  steps: WizardStep[];
  onComplete: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [nextEnabled, setNextEnabled] = useState(true);
  const [nextLabel, setNextLabel] = useState<ReactNode>('Next');
  const [submitting, setSubmitting] = useState(false);
  const handlerRef = useRef<NextHandler | null>(null);

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const step = steps[index];

  const advance = useCallback(() => {
    handlerRef.current = null;
    setIndex((n) => n + 1);
    setNextEnabled(true);
    setNextLabel('Next');
  }, []);

  const next = useCallback(async () => {
    setSubmitting(true);
    try {
      const handler = handlerRef.current;
      const ok = handler ? await handler() : true;
      if (!ok) return;
      if (isLast) {
        await onComplete();
      } else {
        advance();
      }
    } finally {
      setSubmitting(false);
    }
  }, [isLast, onComplete, advance]);

  const prev = useCallback(() => {
    handlerRef.current = null;
    if (isFirst) {
      onCancel();
      return;
    }
    setIndex((n) => n - 1);
    setNextEnabled(true);
    setNextLabel('Next');
  }, [isFirst, onCancel]);

  const controller: WizardStepController = {
    prev,
    cancel: onCancel,
    setNextEnabled,
    setNextLabel,
    setNextHandler: (fn) => { handlerRef.current = fn; },
  };

  return (
    <Box>
      {/* Rail */}
      <Stack
        direction="row"
        spacing={0}
        alignItems="center"
        sx={{ mb: 4 }}
      >
        {steps.map((s, i) => (
          <Stack key={s.id} direction="row" spacing={1.5} alignItems="center" sx={{ flex: i === steps.length - 1 ? '0 0 auto' : 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
                bgcolor: i < index ? theme.palette.brand.main : i === index ? theme.palette.brand.lighter : theme.palette.surface.sunken,
                color: i < index ? theme.palette.brand.contrastText : i === index ? theme.palette.brand.dark : theme.palette.text.tertiary,
                border: i === index ? `2px solid ${theme.palette.brand.main}` : 'none',
                transition: `background-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
              }}
            >
              {i < index ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
            </Box>
            <Stack spacing={0} sx={{ minWidth: 0, flexShrink: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: i === index ? 'text.primary' : 'text.tertiary',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.title}
              </Typography>
              {s.hint && (
                <Typography variant="caption" color="text.tertiary" sx={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                  {s.hint}
                </Typography>
              )}
            </Stack>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 999,
                  mx: 1,
                  bgcolor: i < index ? theme.palette.brand.main : theme.palette.border.subtle,
                  transition: `background-color ${theme.motion.durations.medium}ms ${theme.motion.easings.standard}`,
                }}
              />
            )}
          </Stack>
        ))}
      </Stack>

      {/* Step content */}
      <Box
        sx={{
          bgcolor: theme.palette.surface.card,
          border: `1px solid ${theme.palette.border.subtle}`,
          borderRadius: theme.radius.md + 'px',
          p: { xs: 3, md: 4 },
          minHeight: 280,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step?.id ?? index}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: [0.2, 0, 0, 1] }}
          >
            {step?.content(controller)}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Footer */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        {isFirst ? (
          // No "back" on the first step — would just close the dialog; the
          // explicit Cancel covers that without confusing users.
          <Box />
        ) : (
          <Button
            onClick={prev}
            disabled={submitting}
            startIcon={<BackIcon fontSize="small" />}
            aria-label="previous step"
          >
            Back
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={next}
            disabled={!nextEnabled || submitting}
            sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
          >
            {submitting ? 'Working…' : isLast ? 'Finish' : nextLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
